/**
 * Homepage Hero V2 (FLYR-609) — decorative currency-flow canvas.
 *
 * Animated sine-wave "rails" with currency-glyph circles riding them, drawn
 * behind the hero copy. Ported from the approved prototype
 * (/Users/rodrigo/Code/Flywire/homepage-v2/index.html), adapted to size to the
 * hero SECTION box (not the window) so it stays clipped under the nav.
 *
 * Decorative only (canvas is aria-hidden); the static navy gradient is the
 * no-JS fallback. Mobile (<=900px) keeps the lines but draws 0 circles.
 * Respects prefers-reduced-motion: when reduced, draws a single static frame
 * instead of animating. Imported by src/js/homepage.js — no Vite entry, no
 * template register.
 */
const canvas = document.querySelector('[data-hero-canvas]');

if (canvas) {
    const section = canvas.parentElement;
    const ctx = canvas.getContext('2d');
    const blueRGB = '20, 123, 209';
    // Glyph -> Font Awesome solid icon filename (in /media/svg/). The animated
    // currency set is the keys of this map, in insertion order — one list, so
    // a glyph can never exist without its icon (or vice versa).
    const coinIcons = {
        $: 'dollar-sign',
        '€': 'euro-sign',
        '£': 'sterling-sign',
        '¥': 'yen-sign',
        '₹': 'indian-rupee-sign',
        '₩': 'won-sign',
        '₺': 'turkish-lira-sign',
        '₦': 'naira-sign',
        '฿': 'baht-sign',
        '₱': 'peso-sign',
    };
    const currencies = Object.keys(coinIcons);
    const numLines = 5;
    const numBgLines = 9;

    let width;
    let height;
    let lines = [];
    let bgLines = [];
    let circles = [];
    let rafId = null;

    // Each currency renders as an SVG "coin": a blue disc with the matching
    // Font Awesome solid glyph (white) centered on top. The FA source SVGs live
    // in /media/svg/<name>.svg (Font Awesome Free, CC BY 4.0); we fetch each,
    // scale its path into the 100×100 coin box, and preload the composed coin
    // into an Image drawn at the circle's animated radius/opacity. Until a
    // coin's icon loads, drawCoin() falls back to a flat disc + the unicode
    // glyph so there is no startup flash.
    const coinImages = {};

    // Glyph height inside the 100×100 coin. FA viewBoxes are 512 tall with
    // varying width; width scales proportionally and is centered, so non-square
    // glyphs never stretch.
    const COIN_GLYPH_HEIGHT = 40;

    // Cap coin peak opacity so the coins sit back with the softened rails rather
    // than reading as the loudest element (FLYR-609: "more background-like").
    const COIN_MAX_OPACITY = 0.6;

    // Rails are drawn from this fraction of the width rightward only, leaving the
    // left clear for the hero copy. Coins already start at center (progress ≥
    // 0.5), so the rails fade in to meet them.
    const LINE_START = 0.25;

    // Coins fade in at centre (progress 0.5) and ride rightward until they travel
    // fully off the right edge (COIN_END, a touch past 1.0), then wrap back to centre
    // — they exit off-screen rather than fading out (FLYR-609). Phase maps to
    // arc-length fraction along the rail, so speed is constant along the curve —
    // coins slow horizontally through steep bends instead of sliding at fixed
    // x-rate.
    const COIN_START = 0.5;
    // End well past the right edge (not just 1.0) so each coin slides entirely
    // off-screen — including its trailing edge — before wrapping, rather than
    // winking out half-off.
    const COIN_END = 1.1;
    const COINS_PER_LINE = 3;

    // Uniform phase-advance rate — every coin on every rail moves at the same
    // constant speed.
    const COIN_SPEED = 0.0015;

    // Entry-timing jitter: each coin's phase sits at its even slot ± half this,
    // re-rolled on every wrap (as a delta, so the slot anchor never drifts) —
    // entries aren't metronome-regular and the pattern varies lap to lap. Capped
    // at half the slot spacing so worst-case same-rail gaps stay positive: no
    // coin can overtake another.
    const COIN_ENTRY_JITTER = 1 / (COINS_PER_LINE * 2);

    // Coins reach full opacity a third of the way along their travel (the normalized
    // position np runs 0 at centre → 1 at the right edge), then hold it until exit.
    // The fade is measured from the coin's actual entry point (entryNp): a jitter
    // re-roll can land a re-entering coin slightly past centre, and fading from
    // there (not from absolute centre) keeps it from popping in mid-opacity.
    const COIN_FADE_FRACTION = 1 / 3;

    function buildCoin(symbol) {
        const img = new Image();
        const name = coinIcons[symbol];
        if (!name) {
            return img;
        }
        // Repaint the static frame once the icon loads (the animated loop
        // repaints continuously, but under reduced motion nothing else will).
        img.onload = () => {
            if (reduceMotion.matches) {
                renderFrame();
            }
        };
        fetch(`/media/svg/${name}.svg`)
            .then((res) => res.text())
            .then((text) => {
                const viewBox = text.match(/viewBox="([^"]+)"/);
                const path = text.match(/<path[^>]*\sd="([^"]+)"/);
                if (!viewBox || !path) {
                    return;
                }
                const [, , vbW, vbH] = viewBox[1].split(/\s+/).map(Number);
                const scale = COIN_GLYPH_HEIGHT / vbH;
                const tx = 50 - (vbW * scale) / 2;
                const ty = 50 - COIN_GLYPH_HEIGHT / 2;
                const svg =
                    '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">' +
                    `<circle cx="50" cy="50" r="47" fill="rgb(${blueRGB})"/>` +
                    `<g transform="translate(${tx} ${ty}) scale(${scale})" fill="#ffffff"><path d="${path[1]}"/></g>` +
                    '</svg>';
                img.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
            })
            // Decorative: on fetch failure leave img src unset so drawCoin keeps
            // using the unicode-glyph fallback rather than rejecting unhandled.
            .catch(() => {});
        return img;
    }

    // Coins are only drawn on desktop (initLines makes 0 circles ≤900px), so
    // defer the icon fetches until circles actually exist — no wasted round
    // trips on mobile. Runs once, lazily, the first time coins are needed
    // (incl. a mobile→desktop resize that crosses the breakpoint).
    let coinsPreloaded = false;
    function preloadCoins() {
        if (coinsPreloaded) {
            return;
        }
        coinsPreloaded = true;
        currencies.forEach((symbol) => {
            coinImages[symbol] = buildCoin(symbol);
        });
    }

    // Pick a glyph that isn't currently held by another coin still near its
    // start (fading in), so two of the same currency never appear together at
    // spawn. Falls back to fully random if all glyphs are already in play. A
    // respawning coin still carries its old glyph here (progress just reset to
    // 0.5), so this also stops a coin repeating its own currency back-to-back.
    function pickSymbol() {
        const young = new Set(circles.filter((c) => c.progress < 0.6).map((c) => c.symbol));
        const available = currencies.filter((s) => !young.has(s));
        const pool = available.length ? available : currencies;
        return pool[Math.floor(Math.random() * pool.length)];
    }

    function initLines() {
        lines = [];
        bgLines = [];
        circles = [];

        const coinsPerLine = window.innerWidth <= 900 ? 0 : COINS_PER_LINE;

        if (coinsPerLine > 0) {
            preloadCoins();
        }

        for (let i = 0; i < numLines; i++) {
            lines.push({
                baseY: height * (0.15 + (0.7 / (numLines - 1)) * i),
                frequency: 0.002 + Math.random() * 0.003,
                phase: Math.random() * Math.PI * 2,
                speed: 0.0012 + Math.random() * 0.0024,
                amplitude: 120 + Math.random() * 150,
                // Random per-line phase so the lines don't pulse coins through
                // the centre in lockstep.
                coinPhase: Math.random(),
            });
        }

        for (let i = 0; i < numBgLines; i++) {
            bgLines.push({
                baseY: height * (0.05 + (0.9 / (numBgLines - 1)) * i),
                frequency: 0.002 + Math.random() * 0.003,
                phase: Math.random() * Math.PI * 2,
                speed: 0.0006 + Math.random() * 0.0018,
                amplitude: 100 + Math.random() * 200,
            });
        }

        // Space each line's coins evenly in phase (offset by the line's coinPhase),
        // each nudged by its entry jitter. With the shared monotonic phase→arc map
        // and the jitter cap, same-line gaps stay positive — none overtakes
        // another. The coin keeps its line for life (no re-roll on respawn).
        for (let li = 0; li < numLines; li++) {
            for (let k = 0; k < coinsPerLine; k++) {
                const jitter = (Math.random() - 0.5) * COIN_ENTRY_JITTER;
                const phase = (k / coinsPerLine + lines[li].coinPhase + jitter + 1) % 1;
                circles.push({
                    lineIndex: li,
                    phase,
                    jitter,
                    progress: COIN_START,
                    entryNp: 0,
                    baseRadius: 10 + Math.random() * 5,
                    maxGrowth: 15 + Math.random() * 10,
                    symbol: null,
                });
            }
        }

        placeCoins();
        // Assign glyphs only after every coin is placed, so pickSymbol() sees real
        // positions and avoids duplicates among coins starting together.
        circles.forEach((circle) => {
            circle.symbol = pickSymbol();
        });
    }

    function resize() {
        // Size to the hero section box, not the window, so the canvas stays
        // clipped to the clamp() hero beneath the nav.
        width = canvas.width = section.clientWidth;
        height = canvas.height = section.clientHeight;
        initLines();
        // Under reduced motion no loop is running, so repaint the static frame.
        if (reduceMotion.matches) {
            renderFrame();
        }
    }

    function lineY(line, x) {
        const progress = x / width;
        // Clamp the base to ≥0: past the right edge (x > width) 1 - progress goes
        // negative and Math.pow(neg, 2.5) is NaN, which would blank the coin the
        // instant its centre crossed the edge. Clamped, the rail just flattens to
        // baseY there, so a coin glides straight off the canvas.
        const damping = Math.pow(Math.max(0, 1 - progress), 2.5);
        const baseWave = Math.sin(x * line.frequency + line.phase);
        const chaoticWave = Math.sin(x * (line.frequency * 5) + line.phase * 2) * 1.2;
        return line.baseY + (baseWave + chaoticWave) * (line.amplitude * 1.3) * damping;
    }

    // Arc-length table for the coin travel span (COIN_START → COIN_END) of one
    // rail, sampled at the same 5px step the rails are stroked with. Rebuilt
    // every frame (the wave drifts under the coins), and shared by every coin on
    // the rail, so the phase→position map stays monotonic — the no-overtake
    // guarantee holds.
    function buildArcTable(line) {
        const x0 = width * COIN_START;
        const x1 = width * COIN_END;
        const xs = [x0];
        const ss = [0];
        let prevY = lineY(line, x0);
        let s = 0;
        for (let x = x0 + 5; x <= x1; x += 5) {
            const y = lineY(line, x);
            s += Math.hypot(5, y - prevY);
            xs.push(x);
            ss.push(s);
            prevY = y;
        }
        return { xs, ss, total: s };
    }

    // Invert the table: x at arc length s, interpolating between samples.
    function arcX(table, s) {
        const { xs, ss } = table;
        let lo = 0;
        let hi = ss.length - 1;
        while (lo + 1 < hi) {
            const mid = (lo + hi) >> 1;
            if (ss[mid] <= s) {
                lo = mid;
            } else {
                hi = mid;
            }
        }
        const span = ss[hi] - ss[lo] || 1;
        return xs[lo] + ((xs[hi] - xs[lo]) * (s - ss[lo])) / span;
    }

    // A wrap re-roll can land a coin's phase slightly negative: the coin is
    // waiting off-stage for its re-entry. Waiting coins park at the entry point
    // — still "young" to pickSymbol(), so their glyph stays reserved — and
    // renderFrame() neither draws them nor punches their disc out of the rails.
    function isWaiting(circle) {
        return circle.phase < 0;
    }

    // Place every coin at the arc-length fraction of its rail given by its phase
    // — constant speed along the curve, so coins slow horizontally through steep
    // bends rather than sliding at a fixed x-rate.
    function placeCoins() {
        // No coins (mobile) — skip the per-frame table builds entirely.
        if (!circles.length) {
            return;
        }
        const tables = lines.map((line) => buildArcTable(line));
        circles.forEach((circle) => {
            if (isWaiting(circle)) {
                circle.progress = COIN_START;
                return;
            }
            const table = tables[circle.lineIndex];
            circle.progress = arcX(table, circle.phase * table.total) / width;
            if (circle.entryNp === null) {
                // First on-stage frame since wrapping: record where the coin
                // actually entered so its fade-in starts from here.
                circle.entryNp = Math.max(0, (circle.progress - COIN_START) * 2);
            }
        });
    }

    function strokeLine(line, midStop, endStop, lineWidth) {
        const startX = width * LINE_START;
        ctx.beginPath();
        for (let x = startX; x <= width; x += 5) {
            const y = lineY(line, x);
            if (x === startX) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }
        const grad = ctx.createLinearGradient(startX, 0, width, 0);
        grad.addColorStop(0, `rgba(${blueRGB}, 0.0)`);
        grad.addColorStop(0.5, `rgba(${blueRGB}, ${midStop})`);
        grad.addColorStop(1, `rgba(${blueRGB}, ${endStop})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
    }

    function drawCoin(symbol, x, y, r, op) {
        const img = coinImages[symbol];
        ctx.globalAlpha = op;
        if (img && img.complete && img.naturalWidth) {
            ctx.drawImage(img, x - r, y - r, r * 2, r * 2);
        } else {
            // Fallback until the SVG coin rasterizes: flat disc + glyph.
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = `rgb(${blueRGB})`;
            ctx.fill();
            ctx.fillStyle = '#ffffff';
            // Match the FA coin's glyph size (COIN_GLYPH_HEIGHT of the 100-unit
            // coin, drawn at diameter 2r) so coins don't visibly resize on load.
            ctx.font = `bold ${(r * COIN_GLYPH_HEIGHT) / 50}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(symbol, x, y);
        }
        ctx.globalAlpha = 1;
    }

    // Advance animation state by one tick. Kept separate from renderFrame() so
    // the static / reduced-motion repaints (resize, icon onload) can redraw the
    // current frame without nudging the animation forward.
    function step() {
        bgLines.forEach((line) => {
            line.phase -= line.speed;
        });
        lines.forEach((line) => {
            line.phase -= line.speed;
        });
        circles.forEach((circle) => {
            circle.phase += COIN_SPEED;
            if (circle.phase > 1) {
                // Wrap by one full cycle and re-roll the entry jitter as a delta
                // around the coin's fixed slot — spacing stays anchored (no
                // cumulative drift) while entry timing shifts each lap. A negative
                // resulting phase means the coin waits off-stage briefly before
                // re-entering at centre. Glyph re-picked to avoid duplicating any
                // currency currently starting (and its own glyph).
                const jitter = (Math.random() - 0.5) * COIN_ENTRY_JITTER;
                circle.phase += jitter - circle.jitter - 1;
                circle.jitter = jitter;
                circle.entryNp = null;
                circle.symbol = pickSymbol();
            }
        });
        placeCoins();
    }

    function renderFrame() {
        ctx.clearRect(0, 0, width, height);

        bgLines.forEach((line) => strokeLine(line, 0.08, 0.1, 1));
        lines.forEach((line) => strokeLine(line, 0.2, 0.55, 2));

        const coins = circles
            .filter((circle) => !isWaiting(circle))
            .map((circle) => {
                const x = circle.progress * width;
                const np = (circle.progress - COIN_START) * 2;
                return {
                    symbol: circle.symbol,
                    x,
                    y: lineY(lines[circle.lineIndex], x),
                    // Fade in over a third of travel from the entry point, then hold —
                    // coins exit by moving off the right edge, not by fading out.
                    op: Math.min((np - circle.entryNp) / COIN_FADE_FRACTION, 1) * COIN_MAX_OPACITY,
                    // Grow to full size by the time it's centred (np 1), then hold — so a
                    // coin doesn't keep ballooning while it travels off the edge.
                    r: circle.baseRadius + Math.min(np, 1) * circle.maxGrowth,
                };
            });

        // Two passes: first punch every coin's disc out of the rails drawn above so
        // the line never shows through the translucent disc (the CSS gradient shows
        // behind, not the canvas), then paint the coins. Punching before painting
        // keeps a coin from erasing another it overlaps.
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = '#000';
        coins.forEach((coin) => {
            ctx.beginPath();
            ctx.arc(coin.x, coin.y, coin.r, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.globalCompositeOperation = 'source-over';

        coins.forEach((coin) => drawCoin(coin.symbol, coin.x, coin.y, coin.r, coin.op));
    }

    function loop() {
        step();
        renderFrame();
        rafId = requestAnimationFrame(loop);
    }

    function stop() {
        if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    }

    // Respect prefers-reduced-motion: animate normally when motion is allowed;
    // when reduced, draw a single static frame (frozen rails + coins over the
    // gradient) instead of looping — decorative, but no motion.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function start() {
        stop();
        if (reduceMotion.matches) {
            renderFrame();
        } else {
            loop();
        }
    }

    reduceMotion.addEventListener('change', start);

    resize();
    window.addEventListener('resize', resize);
    start();

    // Tear down on Vite HMR so reloads don't stack rAF loops / listeners.
    if (import.meta.hot) {
        import.meta.hot.dispose(() => {
            stop();
            window.removeEventListener('resize', resize);
            reduceMotion.removeEventListener('change', start);
        });
    }
}
